import { useRef, useState } from 'react';
import { isValidRoomCode } from '@kinop/shared';
import { setNickname, getNickname } from '../lib/storage.js';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

interface HomePageProps {
  onNavigate: (path: string) => void;
}

/** Из ссылки целиком или из голого кода достаём код комнаты. */
function extractRoomCode(raw: string): string {
  const value = raw.trim().toLowerCase();
  return value.includes('/r/') ? (value.split('/r/').pop() ?? '').replace(/\/.*$/, '') : value;
}

export function HomePage({ onNavigate }: HomePageProps): JSX.Element {
  const [joinOpen, setJoinOpen] = useState(false);
  const [joinCode, setJoinCode] = useState('');
  const [joinError, setJoinError] = useState<string | null>(null);
  const joinRef = useRef<HTMLInputElement>(null);

  const handleJoin = (): void => {
    const code = extractRoomCode(joinCode);
    if (!isValidRoomCode(code)) {
      setJoinError('Код состоит из 8 символов. Проверьте ссылку.');
      return;
    }
    // Имя уже могли ввести раньше — тогда комната не спросит его снова.
    if (getNickname().trim()) setNickname(getNickname().trim());
    onNavigate(`/r/${code}`);
  };

  return (
    <main className="home">
      {/* ─────────────────────────── первый экран ─────────────────────────── */}

      <section className="hero">
        <div className="hero__glow" aria-hidden="true" />

        <div className="hero__grid">
          <div>
            <p className="hero__eyebrow">
              <span className="hero__pulse" aria-hidden="true" />
              синхронно у всех · без регистрации
            </p>

            <h1 className="hero__title">
              Один фильм.<br />Разные города.<br /><span style={{color: 'var(--accent)'}}>Один и тот же кадр.</span>
            </h1>

            <p className="hero__lede">
              Комната держит всех на одной секунде — даже когда кто-то отмотал назад или встал за чаем. Реакции и рисунки живут прямо на кадре и тают сами.
            </p>

            <div className="hero__actions">
              <Button size="lg" onClick={() => onNavigate('/new')}>
                Создать комнату
              </Button>
              <Button
                size="lg"
                variant="secondary"
                onClick={() => {
                  setJoinOpen((open) => !open);
                  // Поле появляется сразу под кнопкой — ставим в него курсор,
                  // чтобы не пришлось целиться мышью во второй раз.
                  requestAnimationFrame(() => joinRef.current?.focus());
                }}
                aria-expanded={joinOpen}
              >
                У меня есть ссылка
              </Button>
            </div>

            {joinOpen && (
              <form
                className="hero__join"
                onSubmit={(event) => {
                  event.preventDefault();
                  handleJoin();
                }}
              >
                <Input
                  ref={joinRef}
                  name="join-code"
                  className="font-mono"
                  autoComplete="off"
                  spellCheck={false}
                  autoCapitalize="none"
                  autoCorrect="off"
                  aria-label="Ссылка или код комнаты"
                  aria-invalid={joinError ? true : undefined}
                  placeholder="k7m2xq4p или ссылка целиком…"
                  value={joinCode}
                  onChange={(event) => {
                    setJoinCode(event.target.value);
                    setJoinError(null);
                  }}
                />
                <Button type="submit" variant="contrast">
                  Войти
                </Button>
                {joinError && (
                  <p className="hero__join-error" role="alert">
                    {joinError}
                  </p>
                )}
              </form>
            )}

            <p className="hero__note mono">
              YouTube, Rutube, VK Видео и Vimeo — в родных плеерах
              <br />
              на телефоне — в браузере или Android-приложении
            </p>
          </div>

          {/* Макет комнаты: показывает, чем этот сервис отличается от плеера. */}
          <div className="hero__preview">
            <div className="preview">
              <div className="preview__stage" role="button" tabIndex={0} aria-label="Поставить реакцию на демонстрационный кадр"
              onClick={event => { const el = document.createElement('span'); el.textContent = '🍿'; el.className = 'preview__reaction'; el.style.cssText = `left:${event.nativeEvent.offsetX}px;top:${event.nativeEvent.offsetY}px;animation-iteration-count:1;pointer-events:none`; event.currentTarget.appendChild(el); setTimeout(() => el.remove(), 4400); }}
              onKeyDown={event => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); event.currentTarget.click(); } }}>
                <span className="preview__stub mono">кадр из фильма</span>
                <span className="preview__reaction preview__reaction--1">😂</span>
                <span className="preview__reaction preview__reaction--2">😱</span>
                <span className="preview__reaction preview__reaction--3">🔥</span>
                <div className="preview__bar">
                  <div className="preview__bar-fill" />
                </div>
              </div>
              <div className="preview__foot">
                <div className="preview__faces">
                  <span className="preview__face preview__face--a">Л</span>
                  <span className="preview__face preview__face--b">Б</span>
                  <span className="preview__face preview__face--c">К</span>
                </div>
                <span className="mono preview__caption">3 в комнате · всё синхронно</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─────────────────────────── что умеет ─────────────────────────── */}

      <section className="features">
        <div className="features__grid stagger">
          <article className="feature">
            <div className="feature__num mono">01</div>
            <h3>Смотрим синхронно</h3>
            <p>
              Кто угодно жмёт паузу — стоят все. Отстал на две секунды? Догоним без твоего
              участия.
            </p>
          </article>
          <article className="feature">
            <div className="feature__num mono">02</div>
            <h3>Реакции поверх кадра</h3>
            <p>Эмодзи всплывают там, где ты кликнул. Молча орать вместе — теперь функция.</p>
          </article>
          <article className="feature">
            <div className="feature__num mono">03</div>
            <h3>Рисовалка на видео</h3>
            <p>
              Обвёл ляп в кадре, пририсовал усы герою — стёрлось через пять секунд. Никто не
              пострадал.
            </p>
          </article>
          <article className="feature">
            <div className="feature__num mono">04</div>
            <h3>Комната без аккаунта</h3>
            <p>Ни пароля, ни почты, ни «подтвердите, что вы не робот». Только ссылка.</p>
          </article>
        </div>
      </section>

      {/* ─────────────────────────── цифры ─────────────────────────── */}

      <section className="numbers">
        <div className="numbers__card">
          <p className="numbers__claim">
            Кино на двоих
            <br />
            в 1400 км друг от друга
          </p>

          <dl className="numbers__col">
            <div>
              <dt className="numbers__value mono">API</dt>
              <dd className="numbers__label">синхронизация через API площадок</dd>
            </div>
            <div>
              <dt className="numbers__value mono">48 ч</dt>
              <dd className="numbers__label">живёт комната без активности</dd>
            </div>
          </dl>

          <dl className="numbers__col">
            <div>
              <dt className="numbers__value mono">4</dt>
              <dd className="numbers__label">видеоплощадки с родными плеерами</dd>
            </div>
            <div>
              <dt className="numbers__value mono">0</dt>
              <dd className="numbers__label">полей в регистрации</dd>
            </div>
          </dl>
        </div>
      </section>

      <section className="download-card"><div><h2>Кино всегда рядом</h2><p>Те же комнаты, чат и реакции — в приложении для Android.</p></div><a className="download-link" href="/downloads/smotrim-vmeste.apk" download>↓ Скачать APK</a></section>
      <footer className="site-footer">
        <span className="mono site-footer__copy">
          смотрим вместе · спойлерщиков не обслуживаем
        </span>
        <span className="site-footer__legal">
          Загружайте только то, что вправе распространять: своё видео, домашние архивы или
          материалы с разрешения правообладателя.
        </span>
      </footer>
    </main>
  );
}
